import React, { useState, useRef, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import {
    setDeviceUniqueID,
    setHospitalName,
    setHospitalBranch,
    setHospitalID,
    setAddressOne,
    setAddressTwo,
    setCity,
    setUserState,
    setCountry,
    setPincode,
    setHospitalWebsite,
    setHospitalEmail,
    setHospitalContact,
    setHospitalTelephone,
    setHospitalGST,
    setFileMetadata,
    setName,
    setEmail,
    setMobileNumber,
    setPassword,
    setStateList,
    setOldLogoName,
    setLogoError,
    setCountryList,
} from '@store/reducers/auth/signupSlice';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { MdInfo } from 'react-icons/md';
import { fetchCountries, fetchCountryId, fetchStates } from '@utils/utils';
import { IoEyeOffOutline, IoEyeOutline } from 'react-icons/io5';
import {
    MOBILE_NUMBER_PATTERN,
    MOBILE_NUMBER_TITLE,
    PASSWORD_MIN_Length,
    PASSWORD_MAX_Length,
    PASSWORD_PATTERN,
    PASSWORD_TITLE,
    PINCODE_MAXIMUM_LENGTH,
    PINCODE_MINIMUM_LENGTH,
    PINCODE_PATTERN,
    PINCODE_TITLE,
    MOBILE_NUMBER_MIN_LENGTH,
    MOBILE_NUMBER_MAX_LENGTH,
    NAME_TEXT_MIN_LENGTH,
    NAME_TEXT_MAX_LENGTH,
    NAME_TEXT_PATTERN,
    NAME_TEXT_TITLE,
    HOSPITAL_NAME_TEXT_TITLE,
    DEVICE_NUMBER_MIN_LENGTH,
    DEVICE_NUMBER_MAX_LENGTH,
    DEVICE_NUMBER_TITLE,
    DEVICE_NUMBER_PATTERN,
    HOSPITAL_NAME_PATTERN,
    TELEPHONE_NUMBER_MAX_LENGTH,
    TELEPHONE_NUMBER_MIN_LENGTH,
    TELEPHONE_NUMBER_PATTERN,
    TELEPHONE_NUMBER_TITLE,
    GST_NUMBER_MAX_LENGTH,
    GST_NUMBER_MIN_LENGTH,
    GST_NUMBER_TITLE,
    GST_NUMBER_PATTERN,
    ADDRESS_MAX_LENGTH,
    WEBSITE_PATTERN,
    WEBSITE_TITLE,
    EMAIL_PATTERN,
    EMAIL_TITLE,
    EMAIL_MAX_Length,
} from '@constants/form';

const HospitalForm = () => {
    // const [countryList, setCountryList] = useState<
    //     { id: string; code: string; name: string }[]
    // >([]);
    // const [stateList, setStateList] = useState<{ id: string; name: string }[]>(
    //     []
    // );
    const countryList = useAppSelector((state) => state.signup.countryList);
    const stateList = useAppSelector((state) => state.signup.stateList);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // const [filename, setFileName] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const deviceUniqueID = useAppSelector(
        (state) => state.signup.deviceUniqueID
    );
    const hospitalName = useAppSelector((state) => state.signup.hospital_name);
    const hospitalBranch = useAppSelector(
        (state) => state.signup.hospital_branch
    );
    const hospitalID = useAppSelector((state) => state.signup.hospital_id);
    const address_1 = useAppSelector((state) => state.signup.address_one);
    const address_2 = useAppSelector((state) => state.signup.address_two);
    const city = useAppSelector((state) => state.signup.city);
    const state = useAppSelector((state) => state.signup.userState);
    const country = useAppSelector((state) => state.signup.country);
    const pincode = useAppSelector((state) => state.signup.pincode);
    const hospitalWebsite = useAppSelector(
        (state) => state.signup.hospital_website
    );
    const hospitalEmail = useAppSelector((state) => state.signup.email_id);
    const hospitalContact = useAppSelector((state) => state.signup.contact_no);
    const hospitalGst = useAppSelector((state) => state.signup.gst_no);
    const name = useAppSelector((state) => state.signup.name);
    const email = useAppSelector((state) => state.signup.email);
    const mobileNumber = useAppSelector((state) => state.signup.mobileNumber);
    const password = useAppSelector((state) => state.signup.password);
    const adminCreate = useAppSelector(
        (state) => state.signup.adminCreateHospital
    );
    const logoName = useAppSelector((state) => state.signup.old_logo_name);
    const logo = useAppSelector((state) => state.signup.logo_name);
    const [filename, setFileName] = useState<string | null>(logo);
    const visible = useAppSelector((state) => state.signup.showPassword);
    const [error, setError] = React.useState<string | null>(null);
    const hospitalTelephone = useAppSelector(
        (state) => state.signup.telephoneNumber
    );
    const logoError = useAppSelector((state) => state.signup.logoError);

    useEffect(() => {
        // Only fetch if country list is empty
        if (countryList.length === 0) {
            handleFetchCountryList();
        }
    }, []);

    // Fetch states on load if country has a value (for edit pages, not signup)
    useEffect(() => {
        const fetchStatesForCountry = async () => {
            if (country && countryList.length > 0 && stateList.length === 0) {
                const selectedCountry = countryList.find(
                    (c) => c.name === country
                );
                if (selectedCountry) {
                    await handleFetchStates(selectedCountry.code);
                }
            }
        };
        fetchStatesForCountry();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [country, countryList]);

    const handleFetchCountryList = async () => {
        const list = await fetchCountries();
        if (list.countries) {
            dispatch(setCountryList(list.countries));
        } else if (list.error) {
            console.error('Error fetching countries:', list.error);
        }
    };

    // const handleFetchCountries = async () => {
    //     const list = await fetchCountries();
    //     if (list.countries) {
    //         setCountryList(list.countries);
    //     } else if (list.error) {
    //         // Handle error message
    //         console.error('Error fetching countries:', list.error);
    //     }
    // };

    const handleFetchStates = async (countryId: string) => {
        const result = await fetchStates(countryId);
        if (result.states) {
            dispatch(setStateList(result.states));
        } else if (result.error) {
            // Handle error message
            console.error('Error fetching states:', result.error);
        }
    };

    const handleDeviceUniqueIDChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        // dispatch(setDeviceUniqueID(event.target.value));
        const value = event.target.value;
        if (/^\d*$/.test(value)) {
            dispatch(setDeviceUniqueID(value));
        }
    };

    const handleHospitalName = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setHospitalName(event.target.value));
    };

    const handleHospitalBranch = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        dispatch(setHospitalBranch(event.target.value));
    };

    const handleHospitalID = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setHospitalID(event.target.value));
    };

    const handleAddressOneChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        dispatch(setAddressOne(event.target.value));
    };

    const handleAddressTwoChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        dispatch(setAddressTwo(event.target.value));
    };

    // const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     dispatch(setCity(event.target.value));
    // };
    const handleCityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        // Only allow alphabetic characters (letters) using regex
        const sanitizedValue = inputValue.replace(/[^A-Za-z]/g, '');
        dispatch(setCity(sanitizedValue));
    };

    const handleStateChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        dispatch(setUserState(event.target.value));
    };

    const handleCountryChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const selectedCountry = countryList.find(
            (c) => c.name === event.target.value
        );
        if (selectedCountry) {
            dispatch(setCountry(selectedCountry.name));
            dispatch(setUserState('')); // Clear state when country changes
            dispatch(setStateList([])); // Clear state list
            handleFetchStates(selectedCountry.code); // Fetch states for the selected country
        } else {
            dispatch(setCountry(event.target.value));
            dispatch(setUserState('')); // Clear state when country changes
            dispatch(setStateList([])); // Clear state list
        }
    };

    const handlePincodeChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;
        // Allow only numbers (0-9)
        if (/^\d*$/.test(value) && value.length <= PINCODE_MAXIMUM_LENGTH) {
            dispatch(setPincode(value));
        }
        // dispatch(setPincode(event.target.value));
    };

    const handleHospitalWebsiteChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        dispatch(setHospitalWebsite(event.target.value));
    };

    const handleHospitalEmailChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        dispatch(setHospitalEmail(event.target.value));
    };

    const handleHospitalContactChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;
        // Allow only numbers (0-9)
        if (/^\d*$/.test(value)) {
            dispatch(setHospitalContact(value));
        }
        // dispatch(setHospitalContact(event.target.value));
    };
    const handleHospitalTelephoneChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;
        // Remove non-numeric characters
        const numericValue = value.replace(/\D/g, ''); // Remove all non-numeric characters
        // Update state with the cleaned numericValue
        dispatch(setHospitalTelephone(numericValue));
    };

    const handleHospitalGstChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        // dispatch(setHospitalGST(event.target.value));
        const inputValue = event.target.value.toUpperCase();
        // Only allow alphanumeric characters using regex
        const sanitizedValue = inputValue.replace(/[^A-Za-z0-9]/g, '');
        setHospitalGST(sanitizedValue);
        dispatch(setHospitalGST(sanitizedValue));
        // Check if the value matches the GST number pattern
        if (!/^[0-9A-Z]{15}$/.test(sanitizedValue)) {
            setError(GST_NUMBER_TITLE);
        } else {
            setError(null); // Reset error state if the value is valid
        }
    };
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (event.target.files && file) {
            validateFile(file);
            // dispatch(setHospitalLogo(file));
        }
    };

    const validateFile = (file: File) => {
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
        const maxSizeInBytes = 50 * 1024; // 50KB
        const fileNameWithUnderscores = file.name.replace(/ /g, '_');
        // setFileName(fileNameWithUnderscores);

        if (!validTypes.includes(file.type)) {
            setErrorMessage(
                'Invalid file type. Only PNG, JPEG, and JPG are allowed.'
            );
            clearFileInput();
            return;
        }

        if (file.size > maxSizeInBytes) {
            setErrorMessage('File size exceeds 50KB.');
            clearFileInput();
            return;
        }

        setErrorMessage(null);
        const reader = new FileReader();
        reader.onloadend = () => {
            // console.log('reader.result as string', reader.result as string);
            setFileName(fileNameWithUnderscores);
            dispatch(
                setFileMetadata({
                    name: fileNameWithUnderscores,
                    url: reader.result as string,
                    type: file.type,
                })
            );
            dispatch(setLogoError(false));
        };
        reader.readAsDataURL(file);
    };

    const clearFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        setFileName(null);
    };

    const handleUploadClick = () => {
        // event.preventDefault();
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        // dispatch(setName(event.target.value));
        const value = event.target.value;
        const regex = /^[A-Za-z\s]*$/;
        if (regex.test(value)) {
            dispatch(setName(value));
        }
    };

    const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setEmail(event.target.value));
    };

    const handleMobileNumberChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;
        // Allow only numbers (0-9)
        if (/^\d*$/.test(value)) {
            dispatch(setMobileNumber(value));
        }
        // dispatch(setMobileNumber(event.target.value));
    };

    const handlePasswordChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        dispatch(setPassword(event.target.value));
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword((prevShowPassword) => !prevShowPassword);
    };

    return (
        <>
            <div className="flex justify-start">
                {/* <div className="w-2/4 mr-8">
                    <label htmlFor="device_unique_id" className="mb-2">
                        <div className="text-sm font-medium text-light_grey flex items-center">
                            {t('auth.signup.device_unique_id')}(
                            {t('auth.signup.optional')})
                            <MdInfo className="text-secondary ml-2 text-base" />
                        </div>
                    </label>
                    <input
                        type="text"
                        name="device_unique_id"
                        id="device_unique_id"
                        value={deviceUniqueID}
                        onChange={handleDeviceUniqueIDChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t(
                            'auth.signup.device_unique_id_placeholder'
                        )}
                        pattern={DEVICE_NUMBER_PATTERN}
                        title={DEVICE_NUMBER_TITLE}
                        minLength={DEVICE_NUMBER_MIN_LENGTH}
                        maxLength={DEVICE_NUMBER_MAX_LENGTH}
                    />
                </div> */}
                <div className="w-2/4 mr-8">
                    <label
                        htmlFor="name"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.hospital.hospital_name_lable')}
                        <span className="text-red ml-2">*</span>
                    </label>
                    <input
                        type="text"
                        name="name"
                        id="name"
                        value={hospitalName}
                        onChange={handleHospitalName}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t(
                            'auth.signup.hospital.hospital_name_placeholder'
                        )}
                        minLength={NAME_TEXT_MIN_LENGTH}
                        title={HOSPITAL_NAME_TEXT_TITLE}
                        pattern={HOSPITAL_NAME_PATTERN}
                        maxLength={NAME_TEXT_MAX_LENGTH}
                        required
                    />
                </div>
                <div className="w-2/4">
                    <label
                        htmlFor="hospital_email"
                        className=" mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.hospital.hospital_email_label')}
                    </label>
                    <input
                        type="email"
                        name="hospital_email"
                        id="hospital_email"
                        value={hospitalEmail}
                        onChange={handleHospitalEmailChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t(
                            'auth.signup.hospital.hospital_email_placeholder'
                        )}
                    />
                </div>
            </div>
            <div className="flex justify-start mt-4">
                {/* <div className="w-2/4 mr-8">
                    <label
                        htmlFor="hospital_branch"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.hospital.hospital_branch_label')}
                        <span className="text-red ml-2">*</span>
                    </label>
                    <input
                        type="text"
                        name="hospital_branch"
                        id="hospital_branch"
                        value={hospitalBranch}
                        onChange={handleHospitalBranch}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t(
                            'auth.signup.hospital.hospital_branch_placeholder'
                        )}
                        maxLength={75}
                        required
                    />
                </div> */}
                
                <div className="w-2/4 mr-8">
                    <label
                        htmlFor="address_1"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.address_1_label')}
                        <span className="text-red ml-2">*</span>
                    </label>
                    <input
                        type="text"
                        name="address_1"
                        id="address_1"
                        value={address_1}
                        onChange={handleAddressOneChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t('auth.signup.address_1_palceholder')}
                        maxLength={ADDRESS_MAX_LENGTH}
                        required
                    />
                </div>
                <div className="w-2/4 ">
                    <label
                        htmlFor="address_2"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.address_2_label')}
                        <span className="text-red ml-2">*</span>
                    </label>
                    <input
                        type="text"
                        name="address_2"
                        id="address_2"
                        value={address_2}
                        onChange={handleAddressTwoChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t('auth.signup.address_2_palceholder')}
                        maxLength={ADDRESS_MAX_LENGTH}
                        required
                    />
                </div>
            </div>
            <div className="flex justify-start mt-4">
                <div className="w-2/4 mr-8">
                    <label
                        htmlFor="city"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.city_label')}
                        <span className="text-red ml-2">*</span>
                    </label>
                    <input
                        type="text"
                        name="city"
                        id="city"
                        value={city}
                        onChange={handleCityChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t('auth.signup.city_placeholder')}
                        maxLength={NAME_TEXT_MAX_LENGTH}
                        required
                    />
                </div>
                <div className="w-2/4">
                    <label
                        htmlFor="country"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.country_label')}
                        <span className="text-red ml-2">*</span>
                    </label>
                    <select
                        name="country"
                        id="country"
                        value={country}
                        onChange={handleCountryChange}
                        className="w-full px-3 py-[0.65rem] border rounded placeholder-gains_boro focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                        required
                    >
                        <option
                            className="placeholder-gains_boro"
                            value=""
                            disabled
                        >
                            {t('auth.signup.country_placeholder')}
                        </option>
                        {countryList.map((country) => (
                            <option key={country.code} value={country.name}>
                                {country.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="flex justify-start mt-4">
                <div className="w-2/4  mr-8">
                    <label
                        htmlFor="state"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.state_label')}
                        <span className="text-red ml-2">*</span>
                    </label>
                    <select
                        name="state"
                        id="state"
                        value={state}
                        onChange={handleStateChange}
                        className="w-full px-3 py-[0.65rem] border rounded placeholder-gains_boro focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                        required
                    >
                        <option value="" disabled>
                            {t('auth.signup.state_placeholder')}
                        </option>
                        {stateList.map((state) => (
                            <option key={state.id} value={state.name}>
                                {state.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="w-2/4 ">
                    <label
                        htmlFor="pincode"
                        className=" mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.pincode_label')}
                        <span className="text-red ml-2">*</span>
                    </label>
                    <input
                        type="text"
                        name="pincode"
                        id="pincode"
                        value={pincode}
                        onChange={handlePincodeChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t('auth.signup.pincode_placeholder')}
                        minLength={PINCODE_MINIMUM_LENGTH}
                        maxLength={PINCODE_MAXIMUM_LENGTH}
                        pattern={PINCODE_PATTERN}
                        title={PINCODE_TITLE}
                        required
                    />
                </div>
            </div>
            <div className="flex justify-start mt-4">
                <div className="w-2/4 mr-8">
                    <label
                        htmlFor="hospital_website"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.hospital.hospital_website_label')}
                    </label>
                    <input
                        type="text"
                        name="hospital_website"
                        id="hospital_website"
                        value={hospitalWebsite}
                        onChange={handleHospitalWebsiteChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        pattern={WEBSITE_PATTERN}
                        title={WEBSITE_TITLE}
                        placeholder={t(
                            'auth.signup.hospital.hospital_website_placeholder'
                        )}
                    />
                </div>
                <div className="w-2/4">
                    <label
                        htmlFor="hospital_telephone_no"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.hospital.hospital_telephone_label')}
                    </label>
                    <input
                        type="text"
                        name="hospital_telephone_no"
                        id="hospital_telephone_no"
                        value={hospitalTelephone}
                        onChange={handleHospitalTelephoneChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t(
                            'auth.signup.hospital.hospital_telephone_placeholder'
                        )}
                        pattern={TELEPHONE_NUMBER_PATTERN}
                        title={TELEPHONE_NUMBER_TITLE}
                        minLength={TELEPHONE_NUMBER_MIN_LENGTH}
                        maxLength={TELEPHONE_NUMBER_MAX_LENGTH}
                    />
                </div>
            </div>
            <div className="flex justify-start mt-4">
                <div className="w-2/4 mr-8">
                    <label
                        htmlFor="hospital_contact"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.hospital.hospital_contact_label')}
                    </label>
                    <input
                        type="text"
                        name="hospital_contact"
                        id="hospital_contact"
                        value={hospitalContact}
                        onChange={handleHospitalContactChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t(
                            'auth.signup.hospital.hospital_contact_placeholder'
                        )}
                        pattern={MOBILE_NUMBER_PATTERN}
                        title={MOBILE_NUMBER_TITLE}
                        minLength={MOBILE_NUMBER_MIN_LENGTH}
                        maxLength={MOBILE_NUMBER_MAX_LENGTH}
                    />
                </div>
                <div className="w-2/4">
                    <label
                        htmlFor="hospital_gst"
                        className=" mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.hospital.hospital_gst_label')}(
                        {t('auth.signup.optional')})
                    </label>
                    <input
                        type="text"
                        name="hospital_gst"
                        id="hospital_gst"
                        value={hospitalGst}
                        onChange={handleHospitalGstChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t(
                            'auth.signup.hospital.hospital_gst_placeholder'
                        )}
                        maxLength={GST_NUMBER_MAX_LENGTH}
                        minLength={GST_NUMBER_MIN_LENGTH}
                        title={GST_NUMBER_TITLE}
                    />
                </div>
            </div>
            <div className="flex justify-start mt-4">
                {/* <div className="w-2/4  mr-8">
                    <label
                        htmlFor="hospital_telephone_no"
                        className="mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.hospital.hospital_telephone_label')}
                    </label>
                    <input
                        type="text"
                        name="hospital_telephone_no"
                        id="hospital_telephone_no"
                        value={hospitalTelephone}
                        onChange={handleHospitalTelephoneChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t(
                            'auth.signup.hospital.hospital_telephone_placeholder'
                        )}
                        pattern={TELEPHONE_NUMBER_PATTERN}
                        title={TELEPHONE_NUMBER_TITLE}
                        minLength={TELEPHONE_NUMBER_MIN_LENGTH}
                        maxLength={TELEPHONE_NUMBER_MAX_LENGTH}
                    />
                </div> */}
                {/* <div className="w-2/4">
                    <label
                        htmlFor="hospital_gst"
                        className=" mb-2 text-sm font-medium text-light_grey"
                    >
                        {t('auth.signup.hospital.hospital_gst_label')}(
                        {t('auth.signup.optional')})
                    </label>
                    <input
                        type="text"
                        name="hospital_gst"
                        id="hospital_gst"
                        value={hospitalGst}
                        onChange={handleHospitalGstChange}
                        className="form-control px-3 py-2 placeholder-gains_boro"
                        placeholder={t(
                            'auth.signup.hospital.hospital_gst_placeholder'
                        )}
                        maxLength={GST_NUMBER_MAX_LENGTH}
                        minLength={GST_NUMBER_MIN_LENGTH}
                        title={GST_NUMBER_TITLE}
                    />
                </div> */}
            </div>
            <div className="flex justify-start mt-4">
                <div className="w-2/4 mr-8">
                    <label
                        htmlFor="hospitalLogo"
                        className="block mb-2 text-sm font-medium text-label"
                    >
                        {t('auth.signup.hospital.hospital_logo_label')}
                        {!adminCreate && (
                            <span className="text-red ml-2">*</span>
                        )}
                    </label>
                    <input
                        type="file"
                        name="hospitalLogo"
                        aria-label="hospitalLogo"
                        id="singup_hospitalLogo"
                        accept=".png, .jpeg, .jpg"
                        onChange={handleFileChange}
                        ref={fileInputRef}
                        className="hidden"
                        required={!adminCreate && logoError}
                    />
                    <div className="flex items-center w-28">
                        <div
                            className="text-white !bg-primary font-medium text-sm  px-4 py-2 rounded cursor-pointer text-center"
                            onClick={handleUploadClick}
                        >
                            {t('auth.signup.upload')}
                        </div>
                        <div className="text-base font-medium text-light_grey ml-2">
                            {filename}
                        </div>
                    </div>
                    <span className="text-xs font-normal text-light_grey">
                        {t('auth.signup.logo_msg')}
                    </span>
                    {!adminCreate && logoError && (
                        <div className="text-danger my-2">
                            {t('auth.signup.logo_required')}
                        </div>
                    )}
                </div>
            </div>
            {adminCreate && (
                <>
                    <div className="flex justify-start mt-4">
                        <span className="text-dark_gray text-base font-bold">
                            {t('auth.signup.admin_info')}
                        </span>
                    </div>
                    <div className="flex justify-start mt-3">
                        <div className="w-2/4 mr-8">
                            <label
                                htmlFor="admin_name"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.admin_name_lable')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <input
                                type="text"
                                name="admin_name"
                                id="admin_name"
                                value={name}
                                onChange={handleNameChange}
                                className="form-control px-3 py-2 placeholder-gains_boro"
                                placeholder={t(
                                    'auth.signup.admin_name_placeholder'
                                )}
                                minLength={NAME_TEXT_MIN_LENGTH}
                                maxLength={NAME_TEXT_MAX_LENGTH}
                                title={NAME_TEXT_TITLE}
                                pattern={NAME_TEXT_PATTERN}
                                required
                            />
                        </div>
                        <div className="w-2/4">
                            <label
                                htmlFor="mobile_number"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.mobile_number_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            <input
                                type="text"
                                name="mobile_number"
                                id="mobile_number"
                                value={mobileNumber}
                                onChange={handleMobileNumberChange}
                                className="form-control px-3 py-2 placeholder-gains_boro"
                                placeholder={t(
                                    'auth.signup.mobile_number_placeholder'
                                )}
                                pattern={MOBILE_NUMBER_PATTERN}
                                title={MOBILE_NUMBER_TITLE}
                                minLength={MOBILE_NUMBER_MIN_LENGTH}
                                maxLength={MOBILE_NUMBER_MAX_LENGTH}
                                required
                            />
                        </div>
                    </div>
                    <div className="flex justify-start mt-4">
                        <div className="w-2/4 mr-8">
                            <label
                                htmlFor="email"
                                className="mb-2 text-sm font-medium text-light_grey"
                            >
                                {t('auth.signup.email_id_label')}
                                <span className="text-red ml-2">*</span>
                            </label>
                            {visible ? (
                                <input
                                    type="email"
                                    name="email"
                                    id="signup_email"
                                    value={email}
                                    onChange={handleEmailChange}
                                    className="form-control px-3 py-2 placeholder-gains_boro"
                                    placeholder={t(
                                        'auth.signup.email_id_placeholder'
                                    )}
                                    pattern={EMAIL_PATTERN}
                                    title={EMAIL_TITLE}
                                    maxLength={EMAIL_MAX_Length}
                                    required
                                    autoComplete="off"
                                />
                            ) : (
                                <input
                                    type="email"
                                    name="email"
                                    value={email}
                                    className="form-control px-3 py-2 placeholder-gains_boro"
                                    placeholder={t(
                                        'auth.signup.email_id_placeholder'
                                    )}
                                    readOnly
                                    disabled
                                    autoComplete="off"
                                />
                            )}
                        </div>
                        {visible && (
                            <div className="relative w-2/4">
                                <label
                                    htmlFor="password"
                                    className="mb-2 text-sm font-medium text-light_grey"
                                >
                                    {t('auth.signup.password_label')}
                                </label>
                                <div className="relative flex items-center rounded-md">
                                    <input
                                        type={
                                            showPassword ? 'text' : 'password'
                                        }
                                        name="password"
                                        id="signup_password"
                                        value={password}
                                        onChange={handlePasswordChange}
                                        className="form-control px-3 py-2 placeholder-gains_boro"
                                        placeholder={t(
                                            'auth.signup.password_placeholder'
                                        )}
                                        minLength={PASSWORD_MIN_Length}
                                        maxLength={PASSWORD_MAX_Length}
                                        pattern={PASSWORD_PATTERN}
                                        title={PASSWORD_TITLE}
                                        autoComplete="new-password"
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3 cursor-pointer"
                                        onClick={handleTogglePasswordVisibility}
                                    >
                                        {showPassword ? (
                                            <IoEyeOutline className="text-secondary" />
                                        ) : (
                                            <IoEyeOffOutline className="text-secondary" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </>
    );
};

export default HospitalForm;
